import {
  APP_INITIALIZER,
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from '@app/app.routes';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeEnGb from '@angular/common/locales/en-GB';
import localeEnGbExtra from '@angular/common/locales/extra/en-GB';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeFr from '@angular/common/locales/fr';
import { LanguageService } from '@core/language';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { environment } from '@env/environment';
import { LoaderInterceptor } from '@shared/services/interceptors/loader.interceptor';
import { ErrorHandlerService } from '@app/app-insight/error-handler.service';
import { HttpErrorInterceptor } from '@core/api/interceptors/error.interceptor';
import { OneTrustInitService } from '@core/api/data-services/onetrust/onetrust.service';
import { MaterialModule } from '@shared/material.module';
import {
  NgxUiLoaderConfig,
  NgxUiLoaderHttpModule,
  NgxUiLoaderModule,
} from 'ngx-ui-loader';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import { ApiModule } from '@core/api';
import { VERSION } from '@env/version';
import { TranslateModule } from '@ngx-translate/core';
import { OneTrustModule } from '@altack/ngx-onetrust';
import { datadogRum } from '@datadog/browser-rum';

export function appInitializer(myInitService: OneTrustInitService): () => void {
  return () => myInitService.load();
}

registerLocaleData(localeFr, 'fr-FR', localeFrExtra);
registerLocaleData(localeEnGb, 'en-GB', localeEnGbExtra);

const ngxUiLoaderConfig: NgxUiLoaderConfig = {
  bgsColor: 'transparent',
  bgsOpacity: 0.2,
  bgsPosition: 'center-center',
  bgsSize: 70,
  bgsType: 'ball-spin-clockwise',
  blur: 4,
  fgsColor: '#11273e',
  fgsPosition: 'center-center',
  fgsSize: 70,
  fgsType: 'ball-spin-clockwise',
  gap: 24,
  logoPosition: 'center-center',
  logoSize: 120,
  logoUrl: '',
  masterLoaderId: 'master',
  overlayBorderRadius: '0',
  overlayColor: 'rgba(245,246,250, 0)',
  pbColor: '#0ea8e7',
  pbDirection: 'ltr',
  pbThickness: 1,
  hasProgressBar: false,
};

const datadogEnv = environment.datadog.config.env;

datadogRum.init({
  allowedTracingUrls: [/https:\/\/hera(?:-*\w*)\.sodexo.com/],
  applicationId: environment.datadog.config.applicationId,
  clientToken: environment.datadog.config.clientToken,
  defaultPrivacyLevel: 'mask-user-input',
  env: datadogEnv,
  service: environment.datadog.config.service,
  sessionReplaySampleRate: 20,
  sessionSampleRate: 100,
  site: 'datadoghq.eu',
  trackLongTasks: true,
  trackResources: true,
  trackUserInteractions: true,
  version: `${VERSION.semver.version}${
    datadogEnv !== 'prd' ? '-' + datadogEnv : ''
  }`,
});

datadogRum.startSessionReplayRecording();

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      MaterialModule,
      BrowserAnimationsModule,
      NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
      NgxUiLoaderHttpModule.forRoot({
        showForeground: true,
        excludeRegexp: ['assets', '/tables/', '/positions/'],
      }),
      ApiModule.forRoot(environment.REST_CONFIG, VERSION.semver.version),
      RecaptchaV3Module,
      TranslateModule.forRoot(),
      OneTrustModule.forRoot({
        cookiesGroups: {
          StrictlyNecessaryCookies: 'C0001',
          PerformanceCookies: 'C0002',
          FunctionalCookies: 'C0003',
          TargetingCookies: 'C0004',
          SocialMediaCookies: 'C0005',
        },
        domainScript: environment.cookieDomain,
      })
    ),
    provideRouter(routes),
    {
      provide: LOCALE_ID,
      useValue: 'fr-FR', // Use 'fr-FR' for French, 'en-GB' for English
    },
    {
      provide: LOCALE_ID,
      deps: [LanguageService],
      useFactory: (languageService: { getLanguage: () => string }) =>
        languageService.getLanguage(),
    },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.RECAPTCHA.KEY },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializer,
      deps: [OneTrustInitService],
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
  ],
};
